import { useEffect, useState, useCallback, useRef } from "react";
import { useBlocker } from "react-router-dom";

interface FormProtectionHook {
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
}

export const useFormProtection = (formState: Record<string, any>): FormProtectionHook => {
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const initialFormState = useRef(formState); // Store initial state

  const blocker = useBlocker(isDirty);

  useEffect(() => {
    if (blocker.state === "blocked") {
      const proceed = window.confirm("You have unsaved changes. Are you sure you want to leave?");
      if (proceed) {
        setIsDirty(false);
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker, isDirty]);

  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    if (isDirty) {
      event.preventDefault();
      event.returnValue = "";
    }
  }, [isDirty]);

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [handleBeforeUnload]);

  useEffect(() => {
    const hasChanged = JSON.stringify(formState) !== JSON.stringify(initialFormState.current);
    setIsDirty(hasChanged);
  }, [formState]);

  return { isDirty, setIsDirty };
};
